import { t } from 'i18next';
import { useMutation } from 'react-query';

import { NotificationPosition } from 'core/enums';
import { useHandleErrors } from 'core/hooks';
import { corporatePartPriceService } from 'corporate/services';
import {
  IGetActiveOverlappingPricePayload,
  IRemoveCorporatePartPricePayload,
  IUpsertCorporatePartPricePayload,
} from 'corporate/types';

interface ICorporatePartPriceActionsProps {
  position?: NotificationPosition;
  selectionCount?: number;
}

export const useCorporatePartPriceActions = ({
  position,
  selectionCount,
}: ICorporatePartPriceActionsProps = {}) => ({
  ...useUpsertCorporatePartPrice(position),
  ...useRemoveCorporatePartPrice(selectionCount, position),
  ...useCheckActiveOverlappingPrice(position),
});

const useUpsertCorporatePartPrice = (
  position: NotificationPosition = NotificationPosition.FORM
) => {
  const { errors } = useHandleErrors({
    success: (payload: IUpsertCorporatePartPricePayload) => ({
      message: payload?.id
        ? t('messagePartPriceUpdateSuccess')
        : t('messagePartPriceCreateSuccess'),
    }),
    loading: (payload: IUpsertCorporatePartPricePayload) => ({
      message: payload?.id
        ? t('messagePartPriceUpdateInProgress')
        : t('messagePartPriceCreateInProgress'),
    }),
    error: (payload: IUpsertCorporatePartPricePayload) => ({
      title: payload?.id ? t('errorTitlePartPriceUpdate') : t('errorTitlePartPriceCreate'),
    }),
    position,
    invalidateQueries: ['corporatePartPriceList', 'corporatePart'],
  });

  const { mutateAsync } = useMutation(
    (payload: IUpsertCorporatePartPricePayload) =>
      corporatePartPriceService.upsertCorporatePartPrice(payload),
    { ...errors }
  );

  return { upsertCorporatePartPrice: mutateAsync };
};

const useRemoveCorporatePartPrice = (
  selectionCount = 1,
  position: NotificationPosition = NotificationPosition.DRAWER
) => {
  const { errors } = useHandleErrors({
    success: { message: t('messagePartPricesRemoveSuccess', { count: selectionCount }) },
    loading: { message: t('messagePartPricesRemoveInProgress', { count: selectionCount }) },
    error: { title: t('errorTitlePartPricesRemove', { count: selectionCount }) },
    position,
    invalidateQueries: ['corporatePartPriceList', 'corporatePart'],
  });

  const { mutateAsync } = useMutation(
    (data: IRemoveCorporatePartPricePayload) =>
      corporatePartPriceService.removeCorporatePartPrice(data),
    { ...errors }
  );

  return { removeCorporatePartPrice: mutateAsync };
};

const useCheckActiveOverlappingPrice = (
  position: NotificationPosition = NotificationPosition.FORM
) => {
  const { errors } = useHandleErrors({
    loading: { message: t('messageCheckActiveOverlappingPriceInProgress') },
    error: { title: t('errorTitleCheckActiveOverlappingPrice') },
    position,
  });

  const { mutateAsync } = useMutation(
    (payload: IGetActiveOverlappingPricePayload) =>
      corporatePartPriceService.getActiveOverlappingPrice(payload),
    { ...errors }
  );

  return { checkActiveOverlappingPrice: mutateAsync };
};
